import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  CostAnalysisGroup,
  CostAnalysisView,
  CostDimension,
} from "../../store/apis/costAnalysisApi";
import {
  compact,
  DIMENSION_LABEL,
  money,
  pct,
  unitMoney,
  valueLabel,
  VOLUME_LABEL,
} from "./costAnalysisFormat";

interface Column {
  id: string;
  label: string;
  /** Explicación del dato al pasar por encima de la cabecera. */
  hint?: string;
  sortValue?: (g: CostAnalysisGroup) => number | null;
  render: (g: CostAnalysisGroup) => React.ReactNode;
}

const unitSuffix = (g: CostAnalysisGroup) =>
  g.objective ? ` ${g.objective}` : "";

/** Margen real coloreado frente al planificado: el color nunca va solo, el número está. */
const MarginCell: React.FC<{ group: CostAnalysisGroup }> = ({ group }) => {
  const theme = useTheme();
  const real = group.marginPercent;
  const planned = group.plannedMarginPercent;
  const below = real !== null && planned !== null && real < planned - 2;
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: below ? theme.palette.error.main : undefined,
        }}
      >
        {pct(real)}
      </Typography>
      {planned !== null && (
        <Typography variant="caption" color="text.secondary">
          plan {pct(planned)}
        </Typography>
      )}
    </Box>
  );
};

/** Precio por unidad con la unidad detrás cuando la cabecera no la dice (grupos con objetivos distintos). */
const UnitPrice: React.FC<{
  value: number | null;
  group: CostAnalysisGroup;
  showUnit: boolean;
  caption?: string | null;
}> = ({ value, group, showUnit, caption }) =>
  value === null ? (
    <>—</>
  ) : (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {unitMoney(value)}
        {showUnit && (
          <Typography component="span" variant="caption" color="text.secondary">
            {unitSuffix(group)}
          </Typography>
        )}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      )}
    </>
  );

const buildColumns = (
  targetMargin: number | null | undefined,
  showDelivery: boolean,
  /** Objetivo común a toda la tabla: da nombre a las columnas (CPM compra). null si hay varios. */
  objective: string | null
): Column[] => {
  const showUnit = objective === null;
  return [
    {
      id: "lines",
      label: "Líneas",
      hint: "Líneas de briefing con actividad en el periodo, y clientes distintos",
      sortValue: (g) => g.lines,
      render: (g) => (
        <>
          {g.lines}
          <Typography variant="caption" color="text.secondary" display="block">
            {g.clients} {g.clients === 1 ? "cliente" : "clientes"}
          </Typography>
        </>
      ),
    },
    {
      id: "volume",
      label: "Volumen",
      hint: "Lo que se vende: impresiones en CPM, clics en CPC, vistas completas en CPV…",
      sortValue: (g) => g.volume,
      render: (g) =>
        g.volume === null ? (
          "—"
        ) : (
          <>
            {compact(g.volume)}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {g.objective ? VOLUME_LABEL[g.objective] : ""}
            </Typography>
          </>
        ),
    },
    {
      id: "buyUnit",
      label: objective ? `${objective} compra` : "Compra por unidad",
      hint: "A cuánto se compra: coste real de plataforma por unidad de lo que se vende en el briefing (por mil impresiones en CPM, por clic en CPC…). Debajo, el previsto en el goal",
      sortValue: (g) => g.realUnitCost,
      render: (g) => (
        <UnitPrice
          value={g.realUnitCost}
          group={g}
          showUnit={showUnit}
          caption={
            g.plannedNetUnitCost !== null
              ? `plan ${unitMoney(g.plannedNetUnitCost)}`
              : null
          }
        />
      ),
    },
    {
      id: "saleUnit",
      label: objective ? `${objective} venta` : "Venta por unidad",
      hint: "A cuánto se vende: lo facturado al cliente (coste con el margen diario) por unidad. Debajo, el precio del briefing",
      sortValue: (g) => g.saleUnitCost,
      render: (g) => (
        <UnitPrice
          value={g.saleUnitCost}
          group={g}
          showUnit={showUnit}
          caption={
            g.plannedGrossUnitCost !== null
              ? `briefing ${unitMoney(g.plannedGrossUnitCost)}`
              : null
          }
        />
      ),
    },
    {
      id: "margin",
      label: "Margen real",
      hint: "1 − coste real / venta. Debajo, el margen del plan en el mismo periodo; en rojo si el real queda más de 2 puntos por debajo",
      sortValue: (g) => g.marginPercent,
      render: (g) => <MarginCell group={g} />,
    },
    {
      id: "lineMargin",
      label: "Margen que se pone",
      hint: "Margen puesto en el goal de las líneas del grupo, una vez por línea: la mediana y, debajo, entre qué valores está la mitad de las líneas",
      sortValue: (g) => g.lineMarginMedian,
      render: (g) =>
        g.lineMarginMedian === null ? (
          "—"
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {pct(g.lineMarginMedian)}
            </Typography>
            {g.lineMarginP25 !== null &&
              g.lineMarginP75 !== null &&
              g.lineMarginP25 !== g.lineMarginP75 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {pct(g.lineMarginP25, 0)} – {pct(g.lineMarginP75, 0)}
                </Typography>
              )}
          </>
        ),
    },
    ...(targetMargin !== null && targetMargin !== undefined
      ? [
          {
            id: "target",
            label: `Precio para ${targetMargin} %`,
            hint: "Compra real por unidad / (1 − margen objetivo): el precio de venta que da ese margen sobre lo que cuesta de media",
            sortValue: (g: CostAnalysisGroup) => g.priceAtTargetMargin,
            render: (g: CostAnalysisGroup) => (
              <UnitPrice
                value={g.priceAtTargetMargin}
                group={g}
                showUnit={showUnit}
              />
            ),
          },
        ]
      : []),
    {
      id: "spread",
      label: "Compra entre líneas",
      hint: "Compra por unidad de las líneas del grupo: percentil 25 – percentil 75, y la mediana debajo. Cuanto más ancho, menos fiable es la media",
      sortValue: (g) => g.realUnitCostMedian,
      render: (g) =>
        g.realUnitCostMedian === null ? (
          "—"
        ) : (
          <>
            {unitMoney(g.realUnitCostP25)} – {unitMoney(g.realUnitCostP75)}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              mediana {unitMoney(g.realUnitCostMedian)}
            </Typography>
          </>
        ),
    },
    {
      id: "realCost",
      label: "Coste real",
      hint: "Coste neto de plataforma (lo que nos cobra la plataforma)",
      sortValue: (g) => g.realCost,
      render: (g) => money(g.realCost),
    },
    {
      id: "sale",
      label: "Venta",
      hint: "Coste de cliente con el margen diario aplicado",
      sortValue: (g) => g.sale,
      render: (g) => (
        <>
          {money(g.sale)}
          {g.saleFromPlanShare > 0.005 && (
            <Tooltip
              title={`El ${Math.round(g.saleFromPlanShare * 100)} % de la venta se ha estimado con el margen del plan: esas campañas no tenían margen configurado.`}
            >
              <Chip
                size="small"
                label="estimada"
                sx={{ ml: 0.5, height: 18 }}
              />
            </Tooltip>
          )}
        </>
      ),
    },
    {
      id: "profit",
      label: "Beneficio",
      hint: "Venta − coste real",
      sortValue: (g) => g.grossProfit,
      render: (g) => money(g.grossProfit),
    },
    ...(showDelivery
      ? [
          {
            id: "cpm",
            label: "CPM real",
            sortValue: (g: CostAnalysisGroup) => g.realCpm,
            render: (g: CostAnalysisGroup) => unitMoney(g.realCpm),
          },
          {
            id: "cpc",
            label: "CPC real",
            sortValue: (g: CostAnalysisGroup) => g.realCpc,
            render: (g: CostAnalysisGroup) => unitMoney(g.realCpc),
          },
          {
            id: "cpv",
            label: "CPV real",
            sortValue: (g: CostAnalysisGroup) => g.realCpv,
            render: (g: CostAnalysisGroup) => unitMoney(g.realCpv),
          },
          {
            id: "cpa",
            label: "CPA real",
            sortValue: (g: CostAnalysisGroup) => g.realCpa,
            render: (g: CostAnalysisGroup) => unitMoney(g.realCpa),
          },
          {
            id: "ctr",
            label: "CTR",
            sortValue: (g: CostAnalysisGroup) => g.ctr,
            render: (g: CostAnalysisGroup) => pct(g.ctr, 2),
          },
          {
            id: "vtr",
            label: "VTR",
            sortValue: (g: CostAnalysisGroup) => g.vtr,
            render: (g: CostAnalysisGroup) => pct(g.vtr, 1),
          },
        ]
      : []),
  ];
};

interface Props {
  view: CostAnalysisView;
  showDelivery: boolean;
  /** Filtra por el grupo para ver sus líneas. */
  onShowLines?: (group: CostAnalysisGroup) => void;
}

const CostAnalysisTable: React.FC<Props> = ({
  view,
  showDelivery,
  onShowLines,
}) => {
  const theme = useTheme();
  const groupBy = view.groupBy as CostDimension[];
  // Con un único objetivo en la tabla (filtrado o en el total), las columnas llevan su nombre.
  const objective = view.totals.objective ?? null;
  const columns = useMemo(
    () => buildColumns(view.targetMargin, showDelivery, objective),
    [view.targetMargin, showDelivery, objective]
  );
  const companyNames = useMemo(
    () => new Map(view.companies.map((c) => [c.id, c.name])),
    [view.companies]
  );
  // Sin orden elegido, el del backend: por mes si se agrupa por mes, y de más a menos coste.
  const [sort, setSort] = useState<{ id: string; dir: "asc" | "desc" } | null>(
    null
  );

  const rows = useMemo(() => {
    if (!sort) return view.groups;
    const dims = groupBy as string[];
    const column = columns.find((c) => c.id === sort.id);
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...view.groups].sort((a, b) => {
      if (dims.includes(sort.id)) {
        const d = sort.id as CostDimension;
        return (
          factor *
          valueLabel(d, a.key[d], companyNames).localeCompare(
            valueLabel(d, b.key[d], companyNames),
            "es"
          )
        );
      }
      const va = column?.sortValue?.(a) ?? null;
      const vb = column?.sortValue?.(b) ?? null;
      // Los vacíos siempre al final, en los dos sentidos.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return factor * (va - vb);
    });
  }, [view.groups, sort, columns, groupBy, companyNames]);

  const toggleSort = (id: string, numeric: boolean) =>
    setSort((s) =>
      s?.id === id
        ? { id, dir: s.dir === "asc" ? "desc" : "asc" }
        : { id, dir: numeric ? "desc" : "asc" }
    );

  const headerCell = (
    id: string,
    label: string,
    numeric: boolean,
    hint?: string
  ) => (
    <TableCell
      key={id}
      align={numeric ? "right" : "left"}
      sortDirection={sort?.id === id ? sort.dir : false}
      sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
    >
      {/* describeChild: la explicación describe el botón, no le sustituye el nombre */}
      <Tooltip title={hint ?? ""} disableHoverListener={!hint} describeChild>
        <TableSortLabel
          active={sort?.id === id}
          direction={sort?.id === id ? sort.dir : numeric ? "desc" : "asc"}
          onClick={() => toggleSort(id, numeric)}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  const grouped = groupBy.length > 0;
  // Sin agrupar no hay grupo que abrir: las líneas ya son todas las de lo filtrado.
  const canOpen = grouped && !!onShowLines;

  return (
    <TableContainer
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        maxHeight: "70vh",
      }}
    >
      <Table size="small" stickyHeader aria-label="Grupos">
        <TableHead>
          <TableRow>
            {grouped ? (
              groupBy.map((d) => headerCell(d, DIMENSION_LABEL[d], false))
            ) : (
              <TableCell />
            )}
            {columns.map((c) => headerCell(c.id, c.label, true, c.hint))}
            {canOpen && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Sin agrupar, el único grupo es el total: se pinta solo la fila de total. */}
          {(grouped ? rows : []).map((g, i) => (
            <TableRow key={JSON.stringify(g.key) || i} hover>
              {groupBy.map((d) => (
                <TableCell key={d} sx={{ whiteSpace: "nowrap" }}>
                  {d === "company" && g.companyName
                    ? g.companyName
                    : valueLabel(d, g.key[d], companyNames)}
                </TableCell>
              ))}
              {columns.map((c) => (
                <TableCell
                  key={c.id}
                  align="right"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {c.render(g)}
                </TableCell>
              ))}
              {canOpen && (
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Button
                    size="small"
                    onClick={() => onShowLines?.(g)}
                    aria-label={`Ver las ${g.lines} líneas de ${groupBy
                      .map((d) =>
                        d === "company" && g.companyName
                          ? g.companyName
                          : valueLabel(d, g.key[d], companyNames)
                      )
                      .join(" · ")}`}
                  >
                    Ver líneas
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
          <TableRow
            sx={{
              "& td": {
                fontWeight: 700,
                borderTop: `2px solid ${theme.palette.grey[300]}`,
                position: "sticky",
                bottom: 0,
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            <TableCell colSpan={Math.max(groupBy.length, 1)}>Total</TableCell>
            {columns.map((c) => (
              <TableCell key={c.id} align="right" sx={{ whiteSpace: "nowrap" }}>
                {c.render(view.totals)}
              </TableCell>
            ))}
            {canOpen && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CostAnalysisTable;
